/**
 * Password Reset Request Screen Component
 *
 * This component handles the first step of the password reset flow:
 * allowing users to request a password reset link via email.
 *
 * The component includes:
 * - Email input field with validation
 * - Form submission handling
 * - Success confirmation after sending reset link
 * - Error handling for invalid emails or server issues
 */
import type { Route } from "./+types/forgot-password";

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Form, data } from "react-router";
import { z } from "zod";

import FormButton from "~/core/components/form-button";
import FormErrors from "~/core/components/form-error";
import FormSuccess from "~/core/components/form-success";
import TurnstileWidget from "~/core/components/turnstile-widget";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/core/components/ui/card";
import { Input } from "~/core/components/ui/input";
import { Label } from "~/core/components/ui/label";
import makeServerClient from "~/core/lib/supa-client.server";
import { getRemoteIp, verifyTurnstileToken } from "~/core/lib/turnstile.server";

import { translateAuthErrors } from "../lib/auth-errors";

/**
 * Meta function for the forgot password page
 *
 * Sets the page title using the application name from environment variables
 */
export const meta: Route.MetaFunction = () => {
  return [
    {
      title: `Forgot Password | ${import.meta.env.VITE_APP_NAME}`,
    },
  ];
};

/**
 * Form validation schema for password reset request
 *
 * Uses Zod to validate the email field to ensure it's a valid email format
 * before attempting to send a reset link
 */
const forgotPasswordSchema = z.object({
  email: z.string().email({ message: "invalid_email" }),
});

/**
 * Server action for handling password reset request form submission
 *
 * This function processes the form data and attempts to send a password reset email.
 * The flow is:
 * 1. Parse and validate the email using the schema
 * 2. Return validation errors if the email is invalid
 * 3. Request a password reset email from Supabase auth
 * 4. Return success or error response
 *
 * Note: For security reasons, this endpoint returns success even if the email
 * doesn't exist in the system, to prevent email enumeration attacks.
 *
 * @param request - The form submission request
 * @returns Validation errors, auth errors, or success confirmation
 */
export async function action({ request }: Route.ActionArgs) {
  // Parse and validate form data
  const formData = await request.formData();
  const result = forgotPasswordSchema.safeParse(Object.fromEntries(formData));

  // Return validation errors if email is invalid
  if (!result.success) {
    return data(
      { fieldErrors: result.error.flatten().fieldErrors },
      { status: 400 },
    );
  }

  // Verify Cloudflare Turnstile token before triggering Supabase reset email.
  const turnstileToken = formData.get("cf-turnstile-response");
  const captchaResult = await verifyTurnstileToken(
    typeof turnstileToken === "string" ? turnstileToken : null,
    getRemoteIp(request),
  );
  if (!captchaResult.success) {
    return data({ error: "captcha_failed" }, { status: 400 });
  }

  // Create Supabase client
  const [client] = makeServerClient(request);

  // Request password reset email from Supabase
  const { error } = await client.auth.resetPasswordForEmail(result.data.email);

  // Return error if request fails
  if (error) {
    return data({ error: error.message }, { status: 400 });
  }

  // Return success response
  return { success: true };
}

/**
 * Password Reset Request Component
 *
 * This component renders the form for requesting a password reset link.
 * It includes:
 * - Email input field with validation
 * - Submit button for requesting the reset link
 * - Error display for validation and server errors
 * - Success confirmation message after sending the reset link
 *
 * @param actionData - Data returned from the form action, including errors or success status
 */
export default function ForgotPassword({ actionData }: Route.ComponentProps) {
  const { t } = useTranslation();
  // Reference to the form element for resetting after successful submission
  const formRef = useRef<HTMLFormElement>(null);
  // Track Turnstile token so we can disable submit until the user passes the challenge.
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);

  // Reset the form when the reset link is successfully sent
  useEffect(() => {
    if (actionData && "success" in actionData && actionData.success) {
      formRef.current?.reset();
      formRef.current?.blur();
      setCaptchaToken(null);
    }
  }, [actionData]);
  return (
    <div className="flex items-center justify-center">
      <Card className="w-full max-w-md">
        <CardHeader className="flex flex-col items-center">
          <CardTitle className="text-2xl font-semibold">
            {t("authPage.forgotTitle")}
          </CardTitle>
          <CardDescription className="text-center text-base">
            {t("authPage.forgotDescription")}
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4">
          <Form
            className="flex w-full flex-col gap-5"
            method="post"
            ref={formRef}
          >
            <div className="flex flex-col items-start space-y-2">
              <Label
                htmlFor="email"
                className="flex flex-col items-start gap-1"
              >
                {t("authPage.emailLabel")}
              </Label>
              <Input
                id="email"
                name="email"
                required
                type="email"
                placeholder={t("authPage.emailPlaceholder")}
              />
              {actionData &&
              "fieldErrors" in actionData &&
              actionData.fieldErrors.email ? (
                <FormErrors
                  errors={translateAuthErrors(actionData.fieldErrors.email, t)}
                />
              ) : null}
            </div>
            <TurnstileWidget
              onSuccess={(token) => setCaptchaToken(token)}
              onExpire={() => setCaptchaToken(null)}
              onError={() => setCaptchaToken(null)}
            />
            <FormButton
              label={t("authPage.sendResetLinkButton")}
              className="w-full"
              disabled={!captchaToken}
            />
            {actionData && "error" in actionData && actionData.error ? (
              <FormErrors errors={translateAuthErrors([actionData.error], t)} />
            ) : null}
            {actionData && "success" in actionData && actionData.success ? (
              <FormSuccess message={t("authPage.resetLinkSent")} />
            ) : null}
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
